import { useMemo, useState } from 'react';
import {
  COMPONENT_KINDS,
  COMPONENT_KIND_LABEL,
  COMPONENT_TYPE_SUGGESTIONS,
  buildTemplateRows,
  compareComponents,
  expandNamePattern,
  usedRearPositions,
  type ComponentKind,
  type TemplateDraft,
  type TemplateOwner,
  type TemplateRow,
} from '../../lib/dcim';
import { useAddTemplates, useDeleteTemplate } from '../../hooks/useDcim';
import { BTN, BTN_PRIMARY, INPUT, Feedback, type FeedbackState } from './ui';

/**
 * The component templates of one device type or module type: what every
 * instance receives. Adding a template never touches devices already built.
 */
export function TemplateEditor({
  owner,
  templates,
  allowDeviceBays,
  instanceCount,
}: {
  owner: TemplateOwner;
  templates: TemplateRow[];
  /** Device bays need a type whose subdevice role is "parent". */
  allowDeviceBays: boolean;
  instanceCount: number;
}) {
  const remove = useDeleteTemplate();
  const [message, setMessage] = useState<FeedbackState | null>(null);
  const sorted = useMemo(() => [...templates].sort(compareComponents), [templates]);
  const byId = useMemo(() => new Map(templates.map((t) => [t.id, t])), [templates]);

  const groups = COMPONENT_KINDS.map((kind) => ({ kind, rows: sorted.filter((t) => t.kind === kind) })).filter((g) => g.rows.length);

  const del = async (t: TemplateRow) => {
    setMessage(null);
    try {
      await remove.mutateAsync(t.id);
    } catch (e) {
      setMessage({ tone: 'error', text: `Could not delete ${t.name}: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  return (
    <div className="space-y-4">
      {instanceCount > 0 && (
        <p className="text-xs text-text-muted">
          {instanceCount} device{instanceCount === 1 ? ' was' : 's were'} built from this type. Template changes apply to devices
          built from now on; existing devices keep their components.
        </p>
      )}

      {groups.length === 0 ? (
        <p className="text-sm text-text-muted">No components yet. Add the interfaces, ports and bays from the datasheet below.</p>
      ) : (
        <div className="space-y-3">
          {groups.map((g) => (
            <section key={g.kind} aria-label={COMPONENT_KIND_LABEL[g.kind]}>
              <h4 className="text-xs font-semibold text-text-muted uppercase tracking-wider mb-1">
                {COMPONENT_KIND_LABEL[g.kind]}s ({g.rows.length})
              </h4>
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-xs">
                  <tbody className="divide-y divide-divider bg-surface">
                    {g.rows.map((t) => (
                      <tr key={t.id}>
                        <td className="px-2 py-1.5 font-mono text-text whitespace-nowrap">{t.name}</td>
                        <td className="px-2 py-1.5 text-text-muted">{t.label ?? ''}</td>
                        <td className="px-2 py-1.5 font-mono text-text-muted">{t.type ?? '—'}</td>
                        <td className="px-2 py-1.5 text-text-muted">{templateDetail(t, byId)}</td>
                        <td className="px-2 py-1.5 text-right">
                          <button
                            type="button"
                            onClick={() => void del(t)}
                            disabled={remove.isPending}
                            className="text-xs text-error hover:underline min-h-[32px] px-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded"
                            aria-label={`Delete ${COMPONENT_KIND_LABEL[t.kind]} ${t.name}`}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </div>
      )}
      <Feedback message={message} />

      <AddTemplateForm owner={owner} templates={templates} allowDeviceBays={allowDeviceBays} />
    </div>
  );
}

function templateDetail(t: TemplateRow, byId: Map<string, TemplateRow>): string {
  const bits: string[] = [];
  if (t.mgmt_only) bits.push('management only');
  if (t.poe_mode) bits.push(`PoE ${t.poe_mode.toUpperCase()}`);
  if (t.maximum_draw_w) bits.push(`max ${t.maximum_draw_w} W`);
  if (t.allocated_draw_w) bits.push(`allocated ${t.allocated_draw_w} W`);
  if (t.feed_leg) bits.push(`leg ${t.feed_leg}`);
  if (t.power_port_template_id) bits.push(`fed by ${byId.get(t.power_port_template_id)?.name ?? '?'}`);
  if (t.positions) bits.push(`${t.positions} position${t.positions === 1 ? '' : 's'}`);
  if (t.rear_port_template_id) bits.push(`→ ${byId.get(t.rear_port_template_id)?.name ?? '?'} #${t.rear_port_position}`);
  if (t.position) bits.push(`position ${t.position}`);
  return bits.join(' · ');
}

const EMPTY: TemplateDraft = { kind: 'interface', namePattern: '' };

function AddTemplateForm({ owner, templates, allowDeviceBays }: { owner: TemplateOwner; templates: TemplateRow[]; allowDeviceBays: boolean }) {
  const add = useAddTemplates();
  const [draft, setDraft] = useState<TemplateDraft>(EMPTY);
  const [message, setMessage] = useState<FeedbackState | null>(null);
  const isModule = 'moduleTypeId' in owner;

  const kinds = COMPONENT_KINDS.filter((k) => k !== 'device_bay' || (allowDeviceBays && !isModule));
  const rearPorts = templates.filter((t) => t.kind === 'rear_port');
  const powerPorts = templates.filter((t) => t.kind === 'power_port');
  const used = useMemo(() => usedRearPositions(templates), [templates]);
  const rear = rearPorts.find((r) => r.id === draft.rearPortTemplateId);

  const preview = useMemo(() => {
    try {
      const names = expandNamePattern(draft.namePattern);
      if (names.length <= 1) return null;
      return `${names.length} names: ${names[0]} … ${names[names.length - 1]}`;
    } catch (e) {
      return e instanceof Error ? e.message : String(e);
    }
  }, [draft.namePattern]);

  const set = <K extends keyof TemplateDraft>(key: K, value: TemplateDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setMessage(null);
  };
  const num = (v: string) => (v.trim() === '' ? null : Number(v));

  const chooseRear = (id: string) => {
    setDraft((d) => ({ ...d, rearPortTemplateId: id || null, rearPortStartPosition: id ? (used.get(id) ?? 0) + 1 : null }));
    setMessage(null);
  };

  const submit = async () => {
    const maxSort = templates.reduce((m, t) => Math.max(m, t.sort_order), -1);
    const { rows, error } = buildTemplateRows(owner, draft, { startSortOrder: maxSort + 1, rearPortPositions: rear?.positions ?? null });
    if (error) {
      setMessage({ tone: 'error', text: error });
      return;
    }
    try {
      await add.mutateAsync(rows);
      setMessage({ tone: 'success', text: `Added ${rows.length} ${COMPONENT_KIND_LABEL[draft.kind].toLowerCase()}${rows.length === 1 ? '' : 's'}.` });
      setDraft({ ...EMPTY, kind: draft.kind, type: draft.type });
    } catch (e) {
      setMessage({ tone: 'error', text: `Could not add: ${e instanceof Error ? e.message : String(e)}` });
    }
  };

  const k = draft.kind;
  const suggestions = COMPONENT_TYPE_SUGGESTIONS[k] ?? [];
  const listId = `type-suggestions-${k}`;

  return (
    <form
      className="rounded-md border border-border bg-surface p-3 space-y-3"
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      aria-label="Add component templates"
    >
      <p className="text-xs font-semibold text-text">Add components</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="block">
          <span className="block text-xs text-text-muted mb-1">Kind</span>
          <select value={k} onChange={(e) => setDraft({ ...EMPTY, kind: e.target.value as ComponentKind })} className={INPUT}>
            {kinds.map((kind) => (
              <option key={kind} value={kind}>{COMPONENT_KIND_LABEL[kind]}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="block text-xs text-text-muted mb-1">Name or pattern</span>
          <input value={draft.namePattern} onChange={(e) => set('namePattern', e.target.value)} className={INPUT} placeholder={isModule ? '{module}/[1-24]' : 'Port [1-48]'} required />
          <span className="block text-[11px] text-text-faint mt-0.5">{preview ?? 'Ranges like [1-48], [01-24], [A,B].'}</span>
        </label>
        <label className="block">
          <span className="block text-xs text-text-muted mb-1">Label pattern (optional)</span>
          <input value={draft.labelPattern ?? ''} onChange={(e) => set('labelPattern', e.target.value)} className={INPUT} />
        </label>
        {k !== 'module_bay' && k !== 'device_bay' && (
          <label className="block">
            <span className="block text-xs text-text-muted mb-1">Type</span>
            <input value={draft.type ?? ''} onChange={(e) => set('type', e.target.value)} className={INPUT} list={listId} placeholder={suggestions[0]} />
            <datalist id={listId}>
              {suggestions.map((s) => <option key={s} value={s} />)}
            </datalist>
          </label>
        )}

        {k === 'interface' && (
          <>
            <label className="block">
              <span className="block text-xs text-text-muted mb-1">PoE</span>
              <select value={draft.poeMode ?? ''} onChange={(e) => set('poeMode', e.target.value as TemplateDraft['poeMode'])} className={INPUT}>
                <option value="">None</option>
                <option value="pse">Supplies power (PSE)</option>
                <option value="pd">Powered device (PD)</option>
              </select>
            </label>
            <label className="flex items-center gap-2 text-sm text-text min-h-[44px] md:min-h-[40px] self-end">
              <input type="checkbox" checked={Boolean(draft.mgmtOnly)} onChange={(e) => set('mgmtOnly', e.target.checked)} />
              Management only
            </label>
          </>
        )}

        {k === 'power_port' && (
          <>
            <label className="block">
              <span className="block text-xs text-text-muted mb-1">Maximum draw (W)</span>
              <input inputMode="numeric" value={draft.maximumDrawW ?? ''} onChange={(e) => set('maximumDrawW', num(e.target.value))} className={INPUT} />
            </label>
            <label className="block">
              <span className="block text-xs text-text-muted mb-1">Allocated draw (W)</span>
              <input inputMode="numeric" value={draft.allocatedDrawW ?? ''} onChange={(e) => set('allocatedDrawW', num(e.target.value))} className={INPUT} />
            </label>
          </>
        )}

        {k === 'power_outlet' && (
          <>
            <label className="block">
              <span className="block text-xs text-text-muted mb-1">Fed by power port</span>
              <select value={draft.powerPortTemplateId ?? ''} onChange={(e) => set('powerPortTemplateId', e.target.value || null)} className={INPUT}>
                <option value="">Not specified</option>
                {powerPorts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="block text-xs text-text-muted mb-1">Feed leg</span>
              <select value={draft.feedLeg ?? ''} onChange={(e) => set('feedLeg', e.target.value as TemplateDraft['feedLeg'])} className={INPUT}>
                <option value="">—</option>
                <option value="A">A</option>
                <option value="B">B</option>
                <option value="C">C</option>
              </select>
            </label>
          </>
        )}

        {k === 'rear_port' && (
          <label className="block">
            <span className="block text-xs text-text-muted mb-1">Positions per port</span>
            <input inputMode="numeric" value={draft.positions ?? ''} onChange={(e) => set('positions', num(e.target.value))} className={INPUT} placeholder="1" />
            <span className="block text-[11px] text-text-faint mt-0.5">How many front ports pass through each rear port (e.g. 24 on a panel with one trunk).</span>
          </label>
        )}

        {k === 'front_port' && (
          <>
            <label className="block">
              <span className="block text-xs text-text-muted mb-1">Rear port</span>
              <select value={draft.rearPortTemplateId ?? ''} onChange={(e) => chooseRear(e.target.value)} className={INPUT} required>
                <option value="">Choose…</option>
                {rearPorts.map((r) => (
                  <option key={r.id} value={r.id}>{r.name} ({used.get(r.id) ?? 0}/{r.positions} used)</option>
                ))}
              </select>
              {rearPorts.length === 0 && <span className="block text-[11px] text-warning mt-0.5">Add a rear port first.</span>}
            </label>
            <label className="block">
              <span className="block text-xs text-text-muted mb-1">First position</span>
              <input inputMode="numeric" value={draft.rearPortStartPosition ?? ''} onChange={(e) => set('rearPortStartPosition', num(e.target.value))} className={INPUT} />
            </label>
          </>
        )}

        {k === 'module_bay' && (
          <label className="block">
            <span className="block text-xs text-text-muted mb-1">Position pattern</span>
            <input value={draft.positionPattern ?? ''} onChange={(e) => set('positionPattern', e.target.value)} className={INPUT} placeholder="[A-F]" />
            <span className="block text-[11px] text-text-faint mt-0.5">Replaces {'{module}'} in an installed module's component names.</span>
          </label>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={add.isPending} className={BTN_PRIMARY}>
          {add.isPending ? 'Adding…' : 'Add'}
        </button>
        <button type="button" onClick={() => { setDraft(EMPTY); setMessage(null); }} className={`${BTN} text-text-muted hover:text-text`}>
          Clear
        </button>
      </div>
      <Feedback message={message} />
    </form>
  );
}
